import { NavLink } from "@/components/NavLink";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import ContactForm from "@/components/ContactForm";
const Index = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  // Create session ID if it doesn't exist
  useEffect(() => {
    const initSession = () => {
      let sessionId = sessionStorage.getItem('session_id');
      if (!sessionId) {
        sessionId = crypto.randomUUID();
        sessionStorage.setItem('session_id', sessionId);
      }
      setIsCheckingSession(false);
    };
    initSession();
  }, []);
  useEffect(() => {
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setIsAuthenticated(!!session);
    });
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Skip loading state for better LCP - show content immediately
  // if (isCheckingSession) {
  //   return null;
  // }
  return <div className="min-h-screen bg-background">
        {/* Navigation - Simplified for performance */}
        <nav className="sticky top-0 w-full z-50 border-b bg-background">
          <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <h1 
            className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate("/")}
          >
            The Lost Strings
          </h1>
            <div className="flex gap-3 sm:gap-6 items-center">
              {isAuthenticated ? <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")}>
                  Dashboard
                </Button> : <Button variant="default" size="sm" onClick={() => navigate("/auth")}>
                  Sign In
                </Button>}
            </div>
          </div>
        </nav>

        {/* Hero Section - Optimized for LCP */}
        <section className="pt-24 pb-20 sm:pt-32 sm:pb-32">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight text-foreground">
              Elevate Your Event With Live Music
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              From corporate celebrations to intimate weddings, The Lost Strings delivers world-class musicians for every moment that matters
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6" 
                onClick={() => document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Get Started
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-lg px-8 py-6" 
                onClick={() => document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-10">
                <div className="space-y-1">
                  <div className="text-3xl sm:text-4xl font-bold text-primary">130+</div>
                  <div className="text-sm text-muted-foreground">Musicians</div>
                </div>
                <div className="space-y-1">
                  <div className="text-3xl sm:text-4xl font-bold text-primary">15+</div>
                  <div className="text-sm text-muted-foreground">Instruments</div>
                </div>
                <div className="space-y-1">
                  <div className="text-3xl sm:text-4xl font-bold text-primary">3</div>
                  <div className="text-sm text-muted-foreground">Regions</div>
                </div>
                <div className="space-y-1">
                  <div className="text-3xl sm:text-4xl font-bold text-primary">Global</div>
                  <div className="text-sm text-muted-foreground">Service</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section - Simplified */}
        <section id="services-section" className="py-20 sm:py-32 bg-muted/50">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                  Perfect Music for Every Moment
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  We specialize in crafting unforgettable experiences with live music tailored to your event
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[{
                title: "Cocktail Hours",
                description: "Set the perfect ambiance as guests arrive with sophisticated live music"
              }, {
                title: "Ceremonial Music",
                description: "Make your special moments memorable with our serenading performances"
              }, {
                title: "Corporate Events",
                description: "Elevate product launches, mixers, and company celebrations"
              }, {
                title: "Weddings",
                description: "From ceremony to reception, we create the soundtrack to your love story"
              }, {
                title: "Dancefloor Hype",
                description: "Keep the energy high with live accompaniment that moves the crowd"
              }, {
                title: "Specialty Services",
                description: "Indian instruments including shehnai, dhol, tabla, and more"
              }].map((service, i) => <div key={i} className="p-6 rounded-xl bg-card border border-border">
                    <h3 className="text-xl font-semibold mb-2 text-primary">
                      {service.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {service.description}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* Regional Coverage */}
        <section className="py-20 sm:py-32">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold">
                Coast-to-Coast Coverage
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Operating in three main regions across North America with 130+ musicians ready to perform. Save on accommodation and travel costs with our regional approach.
              </p>
              <div className="grid md:grid-cols-3 gap-6 pt-8">
                <div className="p-6 rounded-xl bg-card border border-border">
                  <h3 className="text-2xl font-bold mb-2">East Region</h3>
                  <p className="text-muted-foreground">Full roster of musicians across the Eastern states</p>
                </div>
                <div className="p-6 rounded-xl bg-card border border-border">
                  <h3 className="text-2xl font-bold mb-2">Central Region</h3>
                  <p className="text-muted-foreground">Serving the heart of North America</p>
                </div>
                <div className="p-6 rounded-xl bg-card border border-border">
                  <h3 className="text-2xl font-bold mb-2">West Region</h3>
                  <p className="text-muted-foreground">Complete coverage of Western states</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact-section" className="py-20 sm:py-32 bg-muted/50">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Get Started</h2>
                <p className="text-lg text-muted-foreground">
                  Tell us about yourself - Step 1 of 2
                </p>
              </div>
              <ContactForm initialData={undefined} initialFormStep={1} />
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t bg-background py-8">
          <div className="container mx-auto px-4 sm:px-6 text-center text-muted-foreground">
            <p>&copy; 2025 The Lost Strings. Creating unforgettable moments through live music.</p>
          </div>
        </footer>
      </div>;
};
export default Index;